import { useState } from 'react';
import { Modal } from '@mui/material';
import { toast } from 'react-toastify';

const AddSystemConfigModal = ({
  isOpen,
  onClose,
  onSave,
  isSubmitting,
}) => {
  const [configType, setConfigType] = useState('');
  const [description, setDescription] = useState('');
  const [groupName, setGroupName] = useState('');
  const [fieldNames, setFieldNames] = useState(['']);
  const [items, setItems] = useState([]);
  const [newItemData, setNewItemData] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [errors, setErrors] = useState({});
  const [addError, setAddError] = useState('');
  const [editError, setEditError] = useState('');

  const resetForm = () => {
    setConfigType('');
    setDescription('');
    setGroupName('');
    setFieldNames(['']);
    setItems([]);
    setNewItemData({});
    setEditingItem(null);
    setErrors({});
    setAddError('');
    setEditError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Strip spaces from config type
  const formatConfigType = (value) => value.replace(/\s+/g, '');

  // Field name management
  const handleAddField = () => setFieldNames((prev) => [...prev, '']);

  const handleRemoveField = (index) => {
    if (fieldNames.length <= 1) return;
    setFieldNames((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFieldNameChange = (index, value) => {
    setFieldNames((prev) => prev.map((f, i) => (i === index ? value : f)));
    if (errors.fieldNames) setErrors((prev) => ({ ...prev, fieldNames: '' }));
  };

  const getValidFields = () => fieldNames.map((f) => f.trim()).filter(Boolean);

  // Duplicate item check
  const isDuplicate = (newItem, excludeIndex = -1) =>
    items.some((item, index) => {
      if (index === excludeIndex) return false;
      return Object.keys(newItem).every((key) => {
        const a = String(newItem[key] || '').toLowerCase().trim();
        const b = String(item[key] || '').toLowerCase().trim();
        return a === b;
      });
    });

  // Add item
  const handleAddItem = () => {
    const fields = getValidFields();
    if (fields.length === 0) return;
    const hasValue = fields.some((f) => newItemData[f]?.trim());
    if (!hasValue) return;

    const newItem = {};
    fields.forEach((f) => { newItem[f] = (newItemData[f] || '').trim(); });

    if (isDuplicate(newItem)) { setAddError('This item already exists'); return; }
    setAddError('');
    setItems((prev) => [...prev, newItem]);
    setNewItemData({});
  };

  const handleDeleteItem = (index) =>
    setItems((prev) => prev.filter((_, i) => i !== index));

  const handleStartEdit = (index, item) => {
    setEditingItem({ index, data: { ...item } });
    setEditError('');
  };

  const handleSaveEdit = () => {
    if (!editingItem) return;
    const { index, data } = editingItem;
    const trimmed = {};
    Object.keys(data).forEach((k) => { trimmed[k] = (data[k] || '').trim(); });
    if (isDuplicate(trimmed, index)) { setEditError('This item already exists'); return; }
    setEditError('');
    setItems((prev) => prev.map((item, i) => (i === index ? trimmed : item)));
    setEditingItem(null);
  };

  const handleCancelEdit = () => { setEditingItem(null); setEditError(''); };

  const updateEditingField = (field, value) => {
    if (editError) setEditError('');
    setEditingItem((prev) => ({ ...prev, data: { ...prev.data, [field]: value } }));
  };

  const updateNewItemField = (field, value) => {
    if (addError) setAddError('');
    setNewItemData((prev) => ({ ...prev, [field]: value }));
  };

  const formatFieldName = (field) =>
    field.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^./, (s) => s.toUpperCase()).trim();

  // Validate and submit
  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    const trimmedType = configType.trim();
    const trimmedGroup = groupName.trim();
    const validFields = getValidFields();

    if (!trimmedType) {
      newErrors.configType = 'Configuration type is required';
    } else if (trimmedType.length > 25) {
      newErrors.configType = 'Max 25 characters';
    }

    if (!trimmedGroup) {
      newErrors.groupName = 'Group name is required';
    }

    if (validFields.length === 0) {
      newErrors.fieldNames = 'At least one field name is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Build the value object
    const valuePayload = {
      description: description.trim(),
      [trimmedGroup]: items,
    };

    const payload = {
      type: trimmedType,
      value: valuePayload,
    };

    onSave(payload);
  };

  const validFields = getValidFields();

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
        <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 w-full md:w-[800px] max-w-4xl max-h-[90vh] overflow-y-auto border border-gray-200 relative">
          {/* Close */}
          <button
            type="button"
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold p-1"
            aria-label="Close"
          >
            &times;
          </button>

          <h2 className="text-2xl font-bold text-secondary-800 mb-6">Add Configuration</h2>

          <form onSubmit={handleSubmit}>
            {/* Config Type */}
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-1">
                Configuration Type <span className="text-danger-500">*</span>
              </label>
              <input
                type="text"
                value={configType}
                maxLength={25}
                onChange={(e) => {
                  setConfigType(formatConfigType(e.target.value));
                  if (errors.configType) setErrors((prev) => ({ ...prev, configType: '' }));
                }}
                placeholder="e.g. DROPDOWN_STATUS"
                className={`w-full border rounded-lg px-4 py-2 outline-none ${
                  errors.configType ? 'border-red-500' : 'border-gray-300 focus:border-secondary-500'
                }`}
              />
              {errors.configType && <p className="text-red-500 text-xs mt-1">{errors.configType}</p>}
              <p className="text-xs text-gray-400 mt-1">{configType.length}/25 characters</p>
            </div>

            {/* Description */}
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-1">Description</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description..."
                className="w-full border border-gray-300 rounded-lg px-4 py-2 outline-none focus:border-secondary-500"
              />
            </div>

            {/* Group Name */}
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-1">
                Group Name <span className="text-danger-500">*</span>
              </label>
              <input
                type="text"
                value={groupName}
                onChange={(e) => {
                  setGroupName(e.target.value);
                  if (errors.groupName) setErrors((prev) => ({ ...prev, groupName: '' }));
                }}
                placeholder="e.g. options, status, items"
                className={`w-full border rounded-lg px-4 py-2 outline-none ${
                  errors.groupName ? 'border-red-500' : 'border-gray-300 focus:border-secondary-500'
                }`}
              />
              {errors.groupName && <p className="text-red-500 text-xs mt-1">{errors.groupName}</p>}
            </div>

            {/* Field Names */}
            <div className="mb-6">
              <label className="block text-gray-700 font-medium mb-1">
                Field Names <span className="text-danger-500">*</span>
              </label>
              <p className="text-xs text-gray-500 mb-2">Define the fields for each item in this group</p>
              {fieldNames.map((field, index) => (
                <div key={index} className="flex items-center gap-2 mb-2">
                  <input
                    type="text"
                    value={field}
                    onChange={(e) => handleFieldNameChange(index, e.target.value)}
                    className={`flex-1 border rounded-lg px-4 py-2 outline-none ${
                      errors.fieldNames ? 'border-red-500' : 'border-gray-300 focus:border-secondary-500'
                    }`}
                    placeholder={`Field name ${index + 1} (e.g. type, name, value)`}
                  />
                  {fieldNames.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveField(index)}
                      className="text-red-500 hover:text-red-700 text-sm px-2 py-1"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              {errors.fieldNames && <p className="text-red-500 text-xs mt-1">{errors.fieldNames}</p>}
              <button
                type="button"
                onClick={handleAddField}
                className="text-secondary-700 hover:text-secondary-800 text-sm font-medium mt-1"
              >
                + Add Field
              </button>
            </div>

            {/* Items Section */}
            {validFields.length > 0 && (
              <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-100 px-4 py-3">
                  <span className="font-medium text-gray-700">
                    {groupName || 'Items'} ({items.length} items)
                  </span>
                </div>

                <div className="max-h-60 overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        {validFields.map((field) => (
                          <th key={field} className="text-left py-2 px-3 text-sm font-semibold text-gray-700">
                            {formatFieldName(field)}
                          </th>
                        ))}
                        <th className="text-center py-2 px-3 text-sm font-semibold text-gray-700 w-24">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan={validFields.length + 1} className="py-4 text-center text-gray-500">
                            No items added yet
                          </td>
                        </tr>
                      ) : (
                        items.map((item, index) => {
                          const isEditing = editingItem?.index === index;
                          return (
                            <tr key={index} className="border-t border-gray-100 hover:bg-gray-50">
                              {isEditing ? (
                                <>
                                  {validFields.map((field, fi) => (
                                    <td key={field} className="py-2 px-3">
                                      <input
                                        type="text"
                                        value={editingItem.data[field] || ''}
                                        onChange={(e) => updateEditingField(field, e.target.value)}
                                        className={`w-full border rounded px-2 py-1 text-sm outline-none ${
                                          editError ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-secondary-500'
                                        }`}
                                      />
                                      {editError && fi === 0 && (
                                        <p className="text-red-500 text-xs mt-1">{editError}</p>
                                      )}
                                    </td>
                                  ))}
                                  <td className="py-2 px-3 text-center">
                                    <div className="flex items-center justify-center gap-1">
                                      <button type="button" onClick={handleSaveEdit} className="text-green-600 hover:text-green-800 text-xs px-2 py-1">Save</button>
                                      <button type="button" onClick={handleCancelEdit} className="text-gray-500 hover:text-gray-700 text-xs px-2 py-1">Cancel</button>
                                    </div>
                                  </td>
                                </>
                              ) : (
                                <>
                                  {validFields.map((field) => (
                                    <td key={field} className="py-2 px-3 text-sm text-gray-700">{item[field] || ''}</td>
                                  ))}
                                  <td className="py-2 px-3 text-center">
                                    <div className="flex items-center justify-center gap-1">
                                      <button type="button" onClick={() => handleStartEdit(index, item)} className="text-secondary-600 hover:text-secondary-800 text-xs px-2 py-1">Edit</button>
                                      <button type="button" onClick={() => handleDeleteItem(index)} className="text-red-500 hover:text-red-700 text-xs px-2 py-1">Delete</button>
                                    </div>
                                  </td>
                                </>
                              )}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Add New Item Row */}
                <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
                  <div className="flex items-center gap-2">
                    {validFields.map((field) => (
                      <input
                        key={field}
                        type="text"
                        value={newItemData[field] || ''}
                        onChange={(e) => updateNewItemField(field, e.target.value)}
                        placeholder={formatFieldName(field)}
                        className={`flex-1 border rounded px-3 py-1.5 text-sm outline-none ${
                          addError ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-secondary-500'
                        }`}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddItem(); } }}
                      />
                    ))}
                    <button
                      type="button"
                      onClick={handleAddItem}
                      className="px-4 py-1.5 rounded-full text-sm font-medium bg-secondary-700 text-white hover:bg-secondary-800 transition"
                    >
                      Add
                    </button>
                  </div>
                  {addError && <p className="text-red-500 text-xs mt-2">{addError}</p>}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="border border-secondary-700 text-secondary-700 py-2 px-6 rounded-full hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !configType.trim()}
                className={`py-2 px-8 rounded-full shadow transition ${
                  !isSubmitting && configType.trim()
                    ? 'bg-secondary-700 text-white hover:bg-secondary-800 cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isSubmitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default AddSystemConfigModal;
